// Client 负责 WS 长连接生命周期、协议帧收发、自动重连、事件分发。
// 不涉及业务认证：连接参数（token 或 dev 字段）由外部的 buildConnectParams 提供。
// 这样 token 刷新等业务关心在 AiKeChat 层处理。

import { v7 as uuidV7 } from 'uuid';
import {
  CtrlTypingPayload,
  Frame,
  FrameType,
  MsgAckPayload,
  MsgRecallPayload,
  MsgRecvPayload,
  MsgSendPayload,
  SysNoticePayload,
} from './protocol';

export interface ClientOptions {
  /** im-gateway WS 入口，例如 ws://localhost:18080/ws */
  serverUrl: string;
  /** 当前用户的 userKey 字符串（用于本地展示，例如 widget 知道发送方是自己） */
  userKey: string;
  /** 每次 dial 调用：返回 url query 参数（token 或 dev 字段）。便于 token 自动刷新。 */
  buildConnectParams: () => Promise<Record<string, string>>;
  /** 心跳间隔（ms），默认 5_000 */
  pingIntervalMs?: number;
  /** 重连初始退避（ms），默认 500 */
  reconnectInitialMs?: number;
  /** 重连最大退避（ms），默认 30_000 */
  reconnectMaxMs?: number;
}

export type ClientEvent =
  | { type: 'open' }
  | { type: 'close'; reason?: string }
  | { type: 'reconnecting'; attempt: number; delay: number }
  | { type: 'message'; payload: MsgRecvPayload; frame: Frame }
  | { type: 'ack'; clientMsgId: string; payload: MsgAckPayload; frame: Frame }
  | { type: 'notice'; payload: SysNoticePayload; frame: Frame }
  | { type: 'typing'; state: 'start' | 'stop'; from: string; frame: Frame }
  | { type: 'read'; lastReadMsgId: string; frame: Frame }
  | { type: 'recall'; payload: MsgRecallPayload; frame: Frame }
  // 排队中连续 5 分钟心跳无法送达：连接判定已断开，不再重连，由 widget 弹提示
  | { type: 'queue-dead' }
  | { type: 'error'; message: string; frame?: Frame };

type EventHandler = (e: ClientEvent) => void;

// 未确认 msg.send 帧的兜底时长：超过仍无 ack 即判定丢失，翻 failed。重连后会自动 flush 重发；
// 上游 PublishUpstream 用 frame.id 幂等（流 duplicate_window=120s），故 120s 内重发被去重，不会重复投递。
const SEND_ACK_TIMEOUT_MS = 20_000;

interface PendingFrame {
  frame: Frame;
  timer: number;
}

export class Client {
  private opts: Required<Omit<ClientOptions, 'buildConnectParams' | 'userKey' | 'serverUrl'>> &
    Pick<ClientOptions, 'buildConnectParams' | 'userKey' | 'serverUrl'>;
  private ws: WebSocket | null = null;
  private handlers = new Set<EventHandler>();
  private pingTimer: number | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  private closedByUser = false;
  // 曾进入过连接流程（connect 被调过）。ensureAlive 只在连过之后探活，避免懒连接下被页面事件强连。
  private hasConnected = false;
  // 最近一次收到任何帧（含服务端 pong）的时刻。看门狗 / ensureAlive 据此判半死连接。
  private lastRecvAt = 0;
  // 已发出但未确认的 msg.send 帧，按 frame.id 索引。重连后 flush 重发，收 ack 出队。
  private pending = new Map<string, PendingFrame>();
  // 排队心跳：仅排队中由 widget 启停。独立于 5s 的 ctrl.ping。
  private queueHbTimer: number | null = null;
  private queueHbConvId: string | null = null;
  private lastQueueBeatOkAt = 0;
  // 排队心跳间隔 10s；连续 5 分钟无法成功送达即判定连接已断开。
  private static readonly QUEUE_HB_INTERVAL_MS = 10_000;
  private static readonly QUEUE_DEAD_MS = 300_000;

  constructor(opts: ClientOptions) {
    this.opts = {
      pingIntervalMs: 5_000,
      reconnectInitialMs: 500,
      reconnectMaxMs: 30_000,
      ...opts,
    };
  }

  get userKey(): string {
    return this.opts.userKey;
  }

  /** 允许 AiKeChat 在 token 刷新后更新 userKey（一般不变，但留作扩展） */
  setUserKey(k: string): void {
    this.opts.userKey = k;
  }

  on(handler: EventHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  private emit(e: ClientEvent) {
    for (const h of this.handlers) {
      try {
        h(e);
      } catch (err) {
        console.error('[leke] handler error:', err);
      }
    }
  }

  connect(): void {
    this.closedByUser = false;
    this.hasConnected = true;
    void this.openSocket();
  }

  /** WebSocket 是否处于 OPEN 状态。AiKeChat 用于延迟连接的判断。 */
  isOpen(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  close(): void {
    this.closedByUser = true;
    this.stopPing();
    this.stopQueueHeartbeat();
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    for (const p of this.pending.values()) clearTimeout(p.timer);
    this.pending.clear();
    if (this.ws) {
      try {
        this.ws.close(1000, 'client closed');
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  /** 发送一条 msg.send 帧。返回 client 端生成的 frame.id（用于关联 ack）。
   *  to 字段在生产模式可留空（由服务端按 conversation 派生对端 userKey）。 */
  send(convId: string, to: string | undefined, payload: MsgSendPayload): string {
    const id = uuidV7();
    const frame: Frame = {
      id,
      type: FrameType.MsgSend,
      ts: Date.now(),
      convId,
      payload,
    };
    if (to) frame.to = to;
    this.trackAndSend(frame);
    return id;
  }

  // 入待确认缓冲并尝试发出；连接非 OPEN 时只入缓冲，重连后由 flushPending 补发。不抛错——避免半死连接丢帧。
  private trackAndSend(f: Frame): void {
    const timer = window.setTimeout(() => {
      if (!this.pending.has(f.id)) return;
      this.pending.delete(f.id);
      // 网关只要 publish 必回 ack；超时无 ack = 该帧从未到达服务端。复用 ack(failed) 让 widget 标红可重发。
      this.emit({
        type: 'ack',
        clientMsgId: f.id,
        payload: { state: 'failed', reason: 'Send timed out. Please try again.' },
        frame: f,
      });
    }, SEND_ACK_TIMEOUT_MS);
    this.pending.set(f.id, { frame: f, timer });
    this.rawSend(f);
  }

  private rawSend(f: Frame): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    try {
      this.ws.send(JSON.stringify(f));
      return true;
    } catch {
      return false;
    }
  }

  private flushPending(): void {
    for (const { frame } of this.pending.values()) this.rawSend(frame);
  }

  private resolvePending(id: string): void {
    const p = this.pending.get(id);
    if (!p) return;
    clearTimeout(p.timer);
    this.pending.delete(id);
  }

  /** 上报最新已读消息 id（msg.read）。服务端写库后会下行 msg.read 到对端。
   *  socket 未就绪时静默丢弃。 */
  sendRead(convId: string, lastReadMsgId: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (!convId || !lastReadMsgId) return;
    const frame: Frame = {
      id: uuidV7(),
      type: FrameType.MsgRead,
      ts: Date.now(),
      convId,
      payload: { lastReadMsgId },
    };
    this.ws.send(JSON.stringify(frame));
  }

  /** 发送 ctrl.typing 帧。to 必填（peer userKey）；服务端不入 NATS，仅转发。
   *  socket 未就绪时静默丢弃。
   *  preview 是尚未发出的输入内容（仅 state='start' 有意义）；服务端会截断到 200 字符。 */
  sendTyping(convId: string, to: string, state: 'start' | 'stop', preview?: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (!to) return;
    const payload: CtrlTypingPayload = { state };
    if (state === 'start' && preview) payload.preview = preview;
    const frame: Frame = {
      id: uuidV7(),
      type: FrameType.CtrlTyping,
      ts: Date.now(),
      convId,
      to,
      payload,
    };
    this.ws.send(JSON.stringify(frame));
  }

  /** 进入排队时由 widget 调：开始每 10s 发排队心跳帧。幂等（重复调用切换 convId）。 */
  startQueueHeartbeat(convId: string): void {
    if (!convId) return;
    if (this.queueHbConvId === convId && this.queueHbTimer != null) return;
    this.stopQueueHeartbeat();
    this.queueHbConvId = convId;
    this.lastQueueBeatOkAt = Date.now();
    this.queueHbTimer = window.setInterval(
      () => this.queueBeatTick(),
      Client.QUEUE_HB_INTERVAL_MS,
    );
  }

  /** 脱队 / 接通 / 会话结束时由 widget 调：停止排队心跳。 */
  stopQueueHeartbeat(): void {
    if (this.queueHbTimer != null) {
      clearInterval(this.queueHbTimer);
      this.queueHbTimer = null;
    }
    this.queueHbConvId = null;
  }

  // queueBeatTick：socket OPEN 则发一帧并记成功时刻；
  // 连续 QUEUE_DEAD_MS 无成功送达（断网/重连失败）→ 判定连接已断开，
  // 停止排队心跳与自动重连，emit queue-dead 让 widget 弹提示。
  private queueBeatTick(): void {
    const convId = this.queueHbConvId;
    if (!convId) return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          id: uuidV7(),
          type: FrameType.CtrlQueueHeartbeat,
          ts: Date.now(),
          convId,
        }),
      );
      this.lastQueueBeatOkAt = Date.now();
      return;
    }
    if (Date.now() - this.lastQueueBeatOkAt >= Client.QUEUE_DEAD_MS) {
      this.stopQueueHeartbeat();
      this.closedByUser = true; // 阻止 onclose 后的自动重连
      if (this.ws) {
        try {
          this.ws.close(1000, 'queue heartbeat dead');
        } catch {
          /* ignore */
        }
        this.ws = null;
      }
      this.emit({ type: 'queue-dead' });
    }
  }

  // ---------- internal ----------

  private async openSocket(): Promise<void> {
    let params: Record<string, string>;
    try {
      params = await this.opts.buildConnectParams();
    } catch (err) {
      this.emit({ type: 'error', message: 'auth failed: ' + String(err) });
      this.scheduleReconnect();
      return;
    }

    const url = new URL(this.opts.serverUrl);
    for (const [k, v] of Object.entries(params)) {
      if (v) url.searchParams.set(k, v);
    }

    const ws = new WebSocket(url.toString());
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.lastRecvAt = Date.now();
      this.startPing();
      // 重连成功：补发断连期间积压 / 发进半死连接的未确认帧。
      this.flushPending();
      this.emit({ type: 'open' });
    };

    ws.onmessage = (ev) => {
      // 收到任何数据都算连接活着（含 pong），供看门狗 / ensureAlive 判半死。
      this.lastRecvAt = Date.now();
      let frame: Frame;
      try {
        frame = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
      } catch (err) {
        this.emit({ type: 'error', message: 'parse frame failed: ' + String(err) });
        return;
      }
      this.dispatch(frame);
    };

    ws.onerror = () => {
      this.emit({ type: 'error', message: 'websocket error' });
    };

    ws.onclose = (ev) => {
      this.stopPing();
      this.ws = null;
      this.emit({ type: 'close', reason: ev.reason || `code=${ev.code}` });
      if (!this.closedByUser) {
        this.scheduleReconnect();
      }
    };
  }

  private dispatch(frame: Frame): void {
    switch (frame.type) {
      case FrameType.MsgAck: {
        const payload = (frame.payload as MsgAckPayload) ?? { state: 'failed' };
        this.resolvePending(frame.id); // 终态：停止重发与超时计时
        this.emit({ type: 'ack', clientMsgId: frame.id, payload, frame });
        break;
      }
      case FrameType.MsgRecv: {
        const payload = frame.payload as MsgRecvPayload;
        this.emit({ type: 'message', payload, frame });
        break;
      }
      case FrameType.CtrlPong:
        break;
      case FrameType.MsgRead: {
        const p = frame.payload as { lastReadMsgId?: string } | undefined;
        this.emit({ type: 'read', lastReadMsgId: p?.lastReadMsgId ?? '', frame });
        break;
      }
      case FrameType.MsgRecall: {
        const payload = (frame.payload as MsgRecallPayload) ?? { msgId: '' };
        this.emit({ type: 'recall', payload, frame });
        break;
      }
      case FrameType.CtrlTyping: {
        const p = frame.payload as { state?: 'start' | 'stop' } | undefined;
        this.emit({
          type: 'typing',
          state: p?.state === 'start' ? 'start' : 'stop',
          from: frame.from ?? '',
          frame,
        });
        break;
      }
      case FrameType.SysError: {
        const p = frame.payload as { code: string; message: string };
        this.emit({ type: 'error', message: `[${p?.code}] ${p?.message}`, frame });
        break;
      }
      case FrameType.SysNotice: {
        const p = (frame.payload as SysNoticePayload) ?? { code: 'server_restart' };
        this.emit({ type: 'notice', payload: p, frame });
        if (p.code === 'server_restart' || p.code === 'server_shutdown') {
          this.softReconnect(p.reconnectAfterMs ?? 500);
        }
        break;
      }
      default:
        this.emit({ type: 'error', message: 'unknown frame type: ' + frame.type, frame });
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      // 看门狗：连续 2 个心跳周期没收到任何帧（含服务端 pong）→ 判半死连接，强制重连。
      // 前台无操作也一直在跑；移动端切后台会冻结本定时器，恢复后第一拍即可能命中。
      if (Date.now() - this.lastRecvAt > this.opts.pingIntervalMs * 2) {
        this.reconnectNow();
        return;
      }
      this.ws.send(
        JSON.stringify({ id: uuidV7(), type: FrameType.CtrlPing, ts: Date.now() }),
      );
    }, this.opts.pingIntervalMs);
  }

  private stopPing(): void {
    if (this.pingTimer != null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  // softReconnect 由 sys.notice 触发：不是用户主动关闭，需要进入重连流程。
  private softReconnect(delayHintMs: number): void {
    if (this.closedByUser) return;
    if (!this.ws) return;
    try {
      this.ws.close(1012, 'server requested restart');
    } catch {
      /* ignore */
    }
    if (delayHintMs > this.opts.reconnectInitialMs) {
      this.opts.reconnectInitialMs = delayHintMs;
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempt += 1;
    const delay = Math.min(
      this.opts.reconnectInitialMs * 2 ** (this.reconnectAttempt - 1),
      this.opts.reconnectMaxMs,
    );
    this.emit({ type: 'reconnecting', attempt: this.reconnectAttempt, delay });
    if (this.reconnectTimer != null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closedByUser) return;
      void this.openSocket();
    }, delay);
  }

  /**
   * 页面恢复可见 / 网络恢复 / 窗口重新聚焦时由上层调用，主动探活——不信任 readyState。
   * 半死连接（readyState 仍 OPEN 但 TCP 已断、onclose 不触发）只能由前台侧主动判定重建。
   * 仅在曾连接过（发过消息触发过 connect）后生效，避免懒连接下被页面事件强行建连。
   */
  ensureAlive(): void {
    if (!this.hasConnected || this.closedByUser) return;
    if (
      !this.ws ||
      this.ws.readyState !== WebSocket.OPEN ||
      Date.now() - this.lastRecvAt > this.opts.pingIntervalMs
    ) {
      this.reconnectNow();
      return;
    }
    // 看起来健康：发一个 ping 探测，后续由 startPing 的看门狗兜底。
    this.ws.send(JSON.stringify({ id: uuidV7(), type: FrameType.CtrlPing, ts: Date.now() }));
  }

  // reconnectNow：清退避定时器 + 摘除旧连接 handler（防其 onclose 再触发一次重连）+ 立即重连、
  // 重置退避。防与 scheduleReconnect 的退避定时器竞争造成双开。
  private reconnectNow(): void {
    if (this.closedByUser) return;
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopPing();
    const old = this.ws;
    this.ws = null;
    if (old) {
      old.onopen = old.onmessage = old.onerror = old.onclose = null;
      try {
        old.close(1000, 'reconnect');
      } catch {
        /* ignore */
      }
    }
    this.reconnectAttempt = 0;
    void this.openSocket();
  }
}
